import { Button, Modal } from '@/components/basic';
import { LoadingSpinner } from '@/components/util/LoadingSpinner';
import { api } from '@/lib/api/api';
import { IResourcePermission } from '@gettactic/api';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

type Props = {
  onClose: () => void;
  permissions: IResourcePermission[];
  resourceIds: string[];
  onDeleted?: () => void;
};

export const DeletePermission = ({
  onClose,
  permissions,
  resourceIds,
  onDeleted
}: Props) => {
  const queryClient = useQueryClient();
  const { handleSubmit } = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const onDeleteSubmit = async () => {
    setLoading(true);

    let deleted = 0;
    let error = 0;

    for (const permission of permissions) {
      const res =
        await api.client.organizations.resourceScheduledPermissionsDelete(
          permission
        );
      if (res.error) {
        error++;
      } else {
        deleted++;
      }
    }

    setLoading(false);
    await Promise.all([
      queryClient.invalidateQueries([
        'organizations.resourceScheduledPermissionsAll'
      ]),
      queryClient.invalidateQueries([
        'organizations.getResourceAdditionalAccess'
      ])
    ]);
    if (error > 0) {
      toast.error("Sorry we couldn't delete all the assignments");
    } else {
      toast.success(
        deleted === 1 ? 'Assignment deleted' : 'Group of assignments deleted'
      );
      onClose();
    }
  };

  return (
    <form id="deleteHour" onSubmit={handleSubmit(onDeleteSubmit)}>
      <div>
        <div className="mt-4">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg
                className="h-6 w-6 text-red-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-base text-gray-800">
                Are you sure you want to delete
                <strong className="font-bold">
                  {permissions.length === 1
                    ? ' this assignment'
                    : ' this group of assignments'}
                </strong>
                ? You cannot restore{' '}
                {permissions.length === 1
                  ? ' this assignment once it has '
                  : ' this group of assignments once they have '}{' '}
                been deleted.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          layout="squared"
          variant="white"
          font="boldGray"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          className="w-full sm:ml-3 sm:w-auto"
          layout="squared"
          variant="primary"
          type="submit"
          form="deleteHour"
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner color="text-white" />
          ) : (
            <span>Delete</span>
          )}
        </Button>
      </Modal.Footer>
    </form>
  );
};

export default DeletePermission;
